import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Factory } from "lucide-react";

const A = `${process.env.PUBLIC_URL}/assets`;

// Fully self-hosted reel — all clips are local files, no third-party branding.
const CLIPS = [
  { video: `${A}/clips/clip-polycoating.mp4`, poster: `${A}/clips/clip-polycoating.jpg`, dur: 5, label: "Poly Coating", sub: "T Die Fall" },
  { video: `${A}/clips/clip-lamination.mp4`, poster: `${A}/clips/clip-lamination.jpg`, dur: 6, label: "Roll to Roll", sub: "Adhesion Treating Process" },
  { video: `${A}/clips/clip-guillotine.mp4`, poster: `${A}/clips/clip-guillotine.jpg`, dur: 9.2, label: "Guillotine Cutting", sub: "Clean-Edge Finish" },
  { video: `${A}/clips/clip-rollover.mp4`, poster: `${A}/clips/clip-rollover.jpg`, dur: 3, label: "Roll Over", sub: "Precise Wind" },
  { video: `${A}/clips/clip-counting.mp4`, poster: `${A}/clips/clip-counting.jpg`, dur: 2, label: "Counting & Packing", sub: "Dispatch Ready" },
];

const ClipVideo = ({ clip }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (v) {
      v.load();
      const tryPlay = () => v.play().catch(() => {});
      tryPlay();
      const t = setTimeout(tryPlay, 600);
      return () => clearTimeout(t);
    }
  }, [clip.video]);
  return (
    <video
      ref={ref}
      src={clip.video}
      poster={clip.poster}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      className="absolute inset-0 w-full h-full object-cover bg-leaf-deep"
    />
  );
};

export default function HeroMontage() {
  const [idx, setIdx] = useState(0);
  const clip = CLIPS[idx];
  const dur = clip.dur;

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % CLIPS.length), dur * 1000 + 800);
    return () => clearInterval(t);
  }, [idx, dur]);

  return (
    <div
      data-testid="hero-montage"
      className="border border-white/20 bg-leaf-deep/60 backdrop-blur-sm shadow-2xl overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/15">
        <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold text-sun">
          <Factory size={13} /> Inside the Factory
        </span>
        <span className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-bold">
          Clip {String(idx + 1).padStart(2, "0")} / {String(CLIPS.length).padStart(2, "0")}
        </span>
      </div>

      <div className="relative aspect-[4/3] overflow-hidden bg-leaf-deep">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {clip.video ? (
              <ClipVideo clip={clip} />
            ) : (
              <motion.img
                src={clip.img}
                alt={`${clip.label} — Lovely Foodpacks manufacturing, Sivakasi`}
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ scale: 1.12, x: 14 }}
                animate={{ scale: 1, x: 0 }}
                transition={{ duration: dur + 0.8, ease: "linear" }}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-leaf-deep/85 via-transparent to-leaf-deep/30 pointer-events-none" />
            <div className="absolute bottom-0 inset-x-0 p-4 pointer-events-none">
              <p className="font-display font-bold text-lg leading-tight text-white">{clip.label}</p>
              <p className="text-white/65 text-xs tracking-wide mt-0.5">{clip.sub}</p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex gap-1 px-4 py-3">
        {CLIPS.map((c, i) => (
          <button
            key={c.label}
            data-testid={`montage-clip-${i}`}
            onClick={() => setIdx(i)}
            aria-label={c.label}
            className="h-1 flex-1 bg-white/20 overflow-hidden"
          >
            {i === idx && (
              <motion.span
                key={`bar-${idx}`}
                className="block h-full bg-sun"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: c.dur + 0.8, ease: "linear" }}
              />
            )}
            {i < idx && <span className="block h-full w-full bg-sun/70" />}
          </button>
        ))}
      </div>
    </div>
  );
}
